import React, { useMemo, useState } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { Image } from 'expo-image';
import PropTypes from 'prop-types';
import { useModalStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import ModelSelectorDetailedDropdown from '@/components/Dropdowns/ModelSelectorDetailedDropdown';
import Tooltip from '@/components/Tooltip';
import useTranslation from '@/hooks/useTranslation';

const createStyles = (colors) => StyleSheet.create({
  triggerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingLeft: 10,
    paddingRight: 6,
    borderRadius: 18,
  },
  triggerButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  singleModelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  modelLogoTrigger: {
    width: 16,
    height: 16,
    borderRadius: 0,
    backgroundColor: '#fff',
  },
  multiModelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  modelImage: {
    width: 20,
    height: 20,
    borderRadius: 0,
    backgroundColor: '#fff',
    borderWidth: 2,
    borderColor: colors.solid_color_primary,
  },
  modelImageOverlap: {
    width: 24,
    height: 24,
    borderColor: colors.solid_color_primary,
    borderRadius: 0,
    marginLeft: -6,
  },
  modelLogoOverlapTrigger: {
    width: 20,
    height: 20,
    borderRadius: 0,
  },
  modelCount: {
    marginLeft: 4,
    fontSize: 14,
    color: colors.text_color_primary,
  },
  modelNameText: {
    maxWidth: 180,
  },
  chevronIcon: {
    marginLeft: 4,
  },
});

const ChatInputModelsButtonTrigger = ({ selectedModels = [], isAutoMode = false, isDisabled = false, isCompact = false, onPress }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [isHovered, setIsHovered] = useState(false);
  const { isXS } = useResponsiveness();
  const showIconOnly = isXS || isCompact;

  return (
    <Tooltip label={t('chatInput.models.tooltip')}>
      <Pressable
        style={[
          styles.triggerButton,
          isHovered && !isDisabled && styles.triggerButtonHovered,
          isDisabled && { opacity: 0.5, cursor: 'not-allowed' },
        ]}
        onHoverIn={() => !isDisabled && setIsHovered(true)}
        onHoverOut={() => !isDisabled && setIsHovered(false)}
        disabled={isDisabled}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={t('chatInput.models.tooltip')}
        accessibilityState={{ disabled: isDisabled }}
      >
        {isAutoMode ? (
          <Text color="primary" size="s">{t('chatInput.models.auto')}</Text>
        ) : (selectedModels?.length || 0) === 0 ? (
          <Text color="primary" size="s">{t('chatInput.models.label')}</Text>
        ) : selectedModels.length === 1 ? (
          <View style={styles.singleModelContainer}>
            {showIconOnly ? (
              <Image
                source={{ uri: selectedModels[0].logoPath }}
                style={styles.modelLogoTrigger}
              />
            ) : (
              <Text color="primary" size="s" numberOfLines={1} style={styles.modelNameText}>{selectedModels[0].name}</Text>
            )}
          </View>
        ) : (
          <View style={styles.multiModelContainer}>
            {selectedModels.slice(0, 3).map((model) => (
              <View
                key={model.id}
                style={[
                  styles.modelImage,
                  selectedModels.length > 1 && styles.modelImageOverlap,
                ]}
              >
                <Image
                  source={{ uri: model.logoPath }}
                  style={styles.modelLogoOverlapTrigger}
                />
              </View>
            ))}
            {selectedModels.length > 3 && (
              <Text style={styles.modelCount} size="xs">+{selectedModels.length - 3}</Text>
            )}
          </View>
        )}
        <View style={styles.chevronIcon}>
          <Icon
            name="chevron-down"
            size={8}
            color={colors.text_color_tertiary}
          />
        </View>
      </Pressable>
    </Tooltip>
  );
};

ChatInputModelsButtonTrigger.propTypes = {
  selectedModels: PropTypes.arrayOf(PropTypes.object),
  isAutoMode: PropTypes.bool,
  isDisabled: PropTypes.bool,
};


function ChatInputModelsButton({
  modelIds,
  onChange,
  maxModels = 5,
  inputs = [],
  outputs = [],
  tags = [],
  shouldCloseOnSelect = true,
  isDisabled,
  selectedModels = [],
  noAutoMode = false,
  noMultiSelectMode = false,
  showDoneButton = false,
  showSelectedModels = false,
  restrictMultipleModelsToSameType = false,
  dropdownSide = 'top', // 'top' for conversation view (input at bottom), 'bottom' for new chat (input at top)
  isCompact = false,
}) {
  const isAutoMode = !modelIds || (Array.isArray(modelIds) && modelIds.length === 0);
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  // Use modal for: non-web, mobile screens, OR new chat view (dropdown doesn't position correctly when input is at top)
  const isNewChatView = dropdownSide === 'bottom';
  const useModalInstead = Platform.OS !== 'web' || isXS || isNewChatView;

  const handleOnOpenModal = () => {
    openModal('ModelSelectorDetailedModal', {
      modelIds,
      onChange,
      maxModels,
      inputs,
      outputs,
      noAutoMode,
      noMultiSelectMode,
      showDoneButton,
      showSelectedModels,
      restrictMultipleModelsToSameType,
      shouldCloseOnSelect,
    });
  };

  if (useModalInstead) {
    return (
      <ChatInputModelsButtonTrigger
        selectedModels={selectedModels}
        onPress={handleOnOpenModal}
        isAutoMode={isAutoMode}
        isDisabled={isDisabled}
        isCompact={isCompact}
      />
    );
  }

  return (
    <ModelSelectorDetailedDropdown
      TriggerComponent={ChatInputModelsButtonTrigger}
      triggerComponentProps={{ selectedModels, isAutoMode, isDisabled, isCompact }}
      modelIds={modelIds}
      onChange={onChange}
      maxModels={maxModels}
      inputs={inputs}
      outputs={outputs}
      tags={tags}
      shouldCloseOnSelect={shouldCloseOnSelect}
      isDisabled={isDisabled}
      noAutoMode={noAutoMode}
      noMultiSelectMode={noMultiSelectMode}
      showDoneButton={showDoneButton}
      showSelectedModels={showSelectedModels}
      restrictMultipleModelsToSameType={restrictMultipleModelsToSameType}
      dropdownSide={dropdownSide}
    />
  );
}

ChatInputModelsButton.propTypes = {
  modelIds: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  onChange: PropTypes.func,
  maxModels: PropTypes.number,
  inputs: PropTypes.arrayOf(PropTypes.string),
  outputs: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  shouldCloseOnSelect: PropTypes.bool,
  isDisabled: PropTypes.bool,
  noAutoMode: PropTypes.bool,
  noMultiSelectMode: PropTypes.bool,
  showDoneButton: PropTypes.bool,
  showSelectedModels: PropTypes.bool,
  restrictMultipleModelsToSameType: PropTypes.bool,
  selectedModels: PropTypes.arrayOf(PropTypes.object),
  dropdownSide: PropTypes.oneOf(['top', 'bottom']),
};

export default React.memo(ChatInputModelsButton);
